<template>
  <form class="login-form" @submit.prevent="handleSubmit">
    <!-- 手机号 -->
    <div class="form-item">
      <label class="form-item-label" for="login-phone">手机号</label>
      <div class="form-item-control">
        <div class="phone-input-wrapper">
          <input
            v-model="formData.phone"
            type="tel"
            id="login-phone"
            class="input-base phone-input"
            placeholder="请输入11位手机号"
            :class="{ 'input-error': errors.phone }"
            $input="validatePhone"
            maxlength="11"
          />
        </div>
        <div class="form-item-error" v-if="errors.phone">
          {{ errors.phone }}
        </div>
      </div>
    </div>

    <!-- 验证码 -->
    <div class="form-item">
      <label class="form-item-label" for="login-code">验证码</label>
      <div class="form-item-control">
        <div class="code-input-wrapper">
          <input
            v-model="formData.code"
            type="text"
            id="login-code"
            class="input-base code-input"
            placeholder="请输入6位验证码"
            :class="{ 'input-error': errors.code }"
            maxlength="6"
          />
          <button
            type="button"
            class="send-code-btn"
            :disabled="countdown > 0 || !isPhoneValid"
            @click="sendCode"
          >
            {{ countdownText }}
          </button>
        </div>
        <div class="form-item-error" v-if="errors.code">{{ errors.code }}</div>
      </div>
    </div>

    <!-- 提交按钮 -->
    <div class="form-item">
      <button
        type="submit"
        class="btn-error submit-btn"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? "登录中..." : "登录" }}
      </button>
    </div>

    <!-- 切换注册 -->
    <div class="switch-form">
      <span>还没有账号？</span>
      <button
        type="button"
        class="switch-btn"
        @click="emit('switch-to-register')"
      >
        立即注册
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { useUserStore } from "@/stores/user";
import { useCommonStore } from "@/stores/common";

const commonStore = useCommonStore();
const userStore = useUserStore();
const router = useRouter();
const emit = defineEmits(["switch-to-register"]);

// 表单数据
const formData = reactive({
  phone: "",
  code: "",
});

// 表单错误
const errors = reactive({
  phone: "",
  code: "",
});

// 倒计时
const countdown = ref(0);
let timer = null;

// 提交状态
const isSubmitting = ref(false);

// 计算属性
const isPhoneValid = computed(() => {
  return /^1[3-9]\d{9}$/.test(formData.phone);
});

const countdownText = computed(() => {
  return countdown.value > 0 ? `${countdown.value}秒后重新获取` : "获取验证码";
});

// 手机号验证
const validatePhone = () => {
  if (!formData.phone) {
    errors.phone = "请输入手机号";
  } else if (!isPhoneValid.value) {
    errors.phone = "手机号格式不正确";
  } else {
    errors.phone = "";
  }
};

// 发送验证码
const sendCode = async () => {
  if (!isPhoneValid.value) {
    errors.phone = "请输入正确的手机号";
    return;
  }
  // 模拟发送验证码
  // 开始倒计时
  commonStore.getVerificationCode(formData.phone).then((res) => {
    if (res.result == "failure") {
      ElMessage.error(res.msg);
      return;
    }
    countdown.value = 60;
    timer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(timer);
        timer = null;
      }
    }, 1000);
  });
};

// 表单提交
const handleSubmit = async () => {
  // 验证表单
  validatePhone();

  if (!formData.code) {
    errors.code = "请输入验证码";
    return;
  }

  if (formData.code.length !== 6) {
    errors.code = "验证码必须为6位数字";
    return;
  }

  // 清除错误
  errors.code = "";
  await userStore.login(formData.phone, formData.code).then((res) => {

    if (res.result=='success') {
      router.push({ name: "patients" });
      ElMessage({
        message: "登录成功",
        type: "success",
      });
    } else {
      // 登录失败
      ElMessage({
        message: res.msg,
        type: "error",
      });
    }
  });
};

// 组件卸载时清除定时器
onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
});
</script>

<style lang="scss" scoped>
@use "../styles/variables.scss" as *;
@use "../styles/common.scss" as *;

.login-form {
  .form-item {
    display: flex;
    .form-item-label {
      width: 90px;
      line-height: 40px;
    }
    &:last-child {
      margin-bottom: 0;
    }
  }

  .phone-input-wrapper {
    display: flex;
    align-items: center;

    .country-code {
      padding: 0 12px;
      height: 40px;
      line-height: 40px;
      background-color: #f5f5f5;
      border: 1px solid $border-color;
      border-right: none;
      border-radius: $border-radius-sm 0 0 $border-radius-sm;
      color: $text-secondary-color;
    }

    .phone-input {
      border-radius: $border-radius-sm;
    }
  }

  .code-input-wrapper {
    display: flex;
    gap: 12px;

    .code-input {
      flex: 1;
    }

    .send-code-btn {
      @include btn-secondary();
      min-width: 120px;
      white-space: nowrap;

      &:disabled {
        background-color: #f5f5f5;
        color: $text-secondary-color;
        border-color: $border-color;
      }
    }
  }

  .submit-btn {
    width: 100%;
    font-size: $font-size-lg;
    height: 44px;
  }

  .switch-form {
    text-align: center;
    margin-top: $margin-base;
    padding-top: $padding-sm;
    border-top: 1px solid $border-color;
    color: $text-secondary-color;

    .switch-btn {
      background: none;
      border: none;
      color: $error-color;
      cursor: pointer;
      font-size: $font-size-base;
      padding: 0;
      margin-left: 4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.input-error {
  border-color: $error-color !important;

  &:focus {
    box-shadow: 0 0 0 2px fade($error-color, 20%) !important;
  }
}
</style>
